import React, { useEffect, useState } from 'react';
import { ArrowRight, Loader2, BookOpenText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const LatestReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/reviews/latest?limit=6`);
        const data = await res.json();
        if (!cancelled) setReviews(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setReviews([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Soft-hide: empty array (RSS unreachable) renders nothing
  if (!loading && reviews.length === 0) return null;

  return (
    <section className="mb-16" data-testid="latest-reviews-section">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <BookOpenText className="w-6 h-6 text-black" />
          <h3 className="font-primary text-3xl md:text-4xl uppercase tracking-wide text-black">
            Latest Reviews
          </h3>
        </div>
        <a
          href="https://www.themoviejunkie.com/blog"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="all-reviews-link"
          className="hidden md:inline-flex items-center gap-1 font-secondary text-sm font-semibold text-black/80 hover:text-black underline decoration-dotted underline-offset-4"
        >
          See all on themoviejunkie.com
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-black/60">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reviews.map((r) => (
            <a
              key={r.link}
              href={r.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`review-card-${(r.link.split('/').pop() || '').slice(0, 60)}`}
              className="group flex flex-col bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border-2 border-black/80 hover:border-black hover:shadow-[6px_6px_0_0_rgba(0,0,0,0.85)] transition-all"
            >
              {r.image_url && (
                <div className="aspect-[16/9] w-full overflow-hidden bg-black/5">
                  <img
                    src={r.image_url}
                    alt={r.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              )}
              <div className="p-5 flex flex-col gap-3 flex-1">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-black/60">
                  {r.category && <span className="px-2 py-0.5 bg-black text-white rounded">{r.category}</span>}
                  <span>{formatDate(r.pub_date)}</span>
                </div>
                <h4 className="font-primary text-lg uppercase tracking-wide text-black leading-tight line-clamp-3">
                  {r.title}
                </h4>
                <p className="font-secondary text-sm text-black/70 line-clamp-3">
                  {r.description}
                </p>
                <div className="mt-auto pt-1 inline-flex items-center gap-1.5 text-sm font-secondary font-semibold text-black/90">
                  Read review
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      <div className="md:hidden mt-5 text-center">
        <a
          href="https://www.themoviejunkie.com/blog"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="all-reviews-link-mobile"
          className="inline-flex items-center gap-1 font-secondary text-sm font-semibold text-black/80 underline decoration-dotted underline-offset-4"
        >
          See all on themoviejunkie.com
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </section>
  );
};

export default LatestReviews;
